import * as R from 'ramda'
import {
  EventDeleteMessagesPayload,
  IMessage,
  Message,
} from '@weblite/ts-action/message'
import { LastOpenedRoomsStateType } from './types'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { isEditMessagePatch } from '$logic/message'
import {
  SET_STORE,
  INSERT,
  REPLACE,
  REMOVE_ROOM,
  CLEAR,
  DELETE_MESSAGE,
  EDIT_MESSAGE,
} from './action'

const ROOM_COUNT_LIMIT = 5

export const lastOpenedRoomsStorageKey = 'lastOpenedRooms'

const initialState: LastOpenedRoomsStateType = {}

const reducers = {
  [SET_STORE]: (
    _: LastOpenedRoomsStateType,
    newState: LastOpenedRoomsStateType,
  ) => ({
    ...newState, // TODO: Remove redux and replace with zustand later
  }),

  [INSERT]: (
    state: LastOpenedRoomsStateType,
    { roomId, messages }: { roomId: string; messages: Message[] },
  ) => {
    const roomOldMessages = state[roomId]?.messages || []
    if (!roomOldMessages.length) return state
    const updatedRooms = {
      ...state,
      [roomId]: {
        messages: [...roomOldMessages, ...messages],
        updatedAt: dateWithOffset(),
      },
    }

    const roomIds = Object.keys(updatedRooms)
    if (roomIds.length > ROOM_COUNT_LIMIT) {
      const lruRoomId = roomIds.reduce((prev, curr) =>
        updatedRooms[curr].updatedAt < updatedRooms[prev].updatedAt
          ? curr
          : prev,
      )
      delete updatedRooms[lruRoomId]
    }

    return updatedRooms
  },

  [REPLACE]: (
    state: LastOpenedRoomsStateType,
    { roomId, messages }: { roomId: string; messages: Message[] },
  ) => {
    const updatedRooms = {
      ...state,
      [roomId]: {
        messages,
        updatedAt: dateWithOffset(),
      },
    }

    const roomIds = Object.keys(updatedRooms)
    if (roomIds.length > ROOM_COUNT_LIMIT) {
      const lruRoomId = roomIds.reduce((prev, curr) =>
        updatedRooms[curr].updatedAt < updatedRooms[prev].updatedAt
          ? curr
          : prev,
      )
      delete updatedRooms[lruRoomId]
    }

    return updatedRooms
  },

  [REMOVE_ROOM]: (state: LastOpenedRoomsStateType, roomId: string) => {
    if (!state[roomId]) return state

    return R.dissoc(roomId, state)
  },

  [CLEAR]: () => initialState,

  [DELETE_MESSAGE]: (
    state: LastOpenedRoomsStateType,
    patch: EventDeleteMessagesPayload,
  ) => {
    const { roomId, iids } = patch
    if (!iids.length) return state

    const roomData = state[roomId]

    if (!roomData) return state

    const updatedMessages = roomData.messages.filter(
      msg => !iids.includes(msg.iid),
    )
    return {
      ...state,
      [roomId]: {
        messages: updatedMessages,
        updatedAt: dateWithOffset(),
      },
    }
  },

  [EDIT_MESSAGE]: (
    state: LastOpenedRoomsStateType,
    {
      roomId,
      iid,
      patch,
    }: {
      roomId: string
      iid: number
      patch: Pick<IMessage, 'body' | 'attachment' | 'pinned'>
    },
  ) => {
    const roomData = state[roomId]
    if (roomData) {
      const updatedMessages = roomData.messages.map(msg =>
        msg.iid === iid && !msg?.patches?.deleted
          ? {
              ...msg,
              ...patch,
              ...(isEditMessagePatch(patch) && {
                patches: { edited: true },
              }),
            }
          : msg,
      )
      return {
        ...state,
        [roomId]: {
          messages: updatedMessages,
          updatedAt: dateWithOffset(),
        },
      }
    }
    return state
  },
}

export const lastOpenedRoomMessagesReducer = (
  state = initialState,
  {
    type,
    payload,
  }: {
    type: keyof typeof reducers
    payload: any
  },
) => (reducers[type] ? reducers[type](state, payload) : state)
